import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useParams } from "react-router-dom";
import {
  Home,
  Users as UsersIcon,
  Mail,
  Settings,
  Layers,
  LayoutDashboard,
  Sliders,
  Gamepad2,
  Shield,
  Bot,
  MessagesSquare,
  Trophy,
  Key,
  ArrowRightLeft,
  MoreHorizontal,
  ChevronRight,
  LogOut,
} from "lucide-react";

import { AppSelector } from "../components/AppSelector/AppSelector";
import { Avatar } from "../components/Avatar/Avatar";
import { Badge } from "../components/Badge/Badge";
import { useAuth } from "../features/auth/useAuth";
import { NetworkBanner } from "../features/errors/NetworkBanner";
import { apiClient } from "../lib/apiClient";
import { useConfigOutline } from "./configOutlineContext";
import styles from "./AppShell.module.css";

export function AppShell() {
  const { user, projects, loading } = useAuth();
  const params = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const projectIdFromRoute = params.id;
  // Config-page section outline (published by ProjectConfigPage). Drives the
  // nested sub-nav under the "Config" item — mockup project-config.html L344-L381.
  const { outline } = useConfigOutline();

  if (loading || !user) {
    return <div className={styles.loading}>Loading…</div>;
  }

  const isSuper = user.role === "super_admin";

  // Project sub-nav appears in the sidebar whenever we're inside a project
  // route. Per mockups (project-overview.html L378-L415, project-config.html,
  // project-team.html, project-api-keys.html, users-list.html, projects-list.html
  // — all show BOTH Project group + Platform group together when there is an
  // active project context). No tabs in the page header.
  const activeProjectId = projectIdFromRoute ?? null;

  async function signOut() {
    try {
      await apiClient.post("/api/admin/auth/logout");
    } catch {
      // even if logout fails server-side, drop client + redirect
    }
    navigate("/login", { replace: true });
  }

  return (
    <>
      <NetworkBanner />
      <div className={styles.shell}>
        <aside className={styles.sidebar} aria-label="Primary">
          <Link to="/" className={styles.brand} aria-label="Social Hub — home">
            <span className={styles.brandMark} aria-hidden="true">
              <Layers size={14} color="#fff" />
            </span>
            <span className={styles.brandName}>Social Hub</span>
          </Link>

          <div className={styles.selector}>
            <AppSelector
              projects={projects.map((p) => ({ id: p.id, name: p.name }))}
              activeId={projectIdFromRoute}
              onSelect={(id) => navigate(`/projects/${id}`)}
            />
          </div>

          {activeProjectId && (
            <nav className={styles.section} aria-label="Project">
              <div className={styles.sectionLabel}>Project</div>
              <SidebarLink
                to={`/projects/${activeProjectId}/overview`}
                icon={<LayoutDashboard size={16} />}
              >
                Overview
              </SidebarLink>
              <SidebarLink
                to={`/projects/${activeProjectId}/config`}
                icon={<Sliders size={16} />}
              >
                Config
                {outline.sections.some((s) => s.dirty) && (
                  <span
                    className={styles.dirtyDot}
                    aria-label="Unsaved changes"
                    title="Unsaved changes"
                  />
                )}
              </SidebarLink>
              {outline.sections.length > 0 && (
                <div className={styles.navSubitems}>
                  {outline.sections.map((s) => (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      className={
                        s.id === outline.activeId
                          ? `${styles.subItem} ${styles.subItemActive}`
                          : styles.subItem
                      }
                      onClick={(e) => {
                        e.preventDefault();
                        document
                          .getElementById(s.id)
                          ?.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                          });
                      }}
                    >
                      <span className={styles.bullet} aria-hidden="true" />
                      <span className={styles.subItemLabel}>{s.label}</span>
                      {s.dirty && (
                        <span
                          className={styles.modDot}
                          aria-label="Modified"
                          title="Modified"
                        />
                      )}
                    </a>
                  ))}
                </div>
              )}
              <SidebarLink
                to={`/projects/${activeProjectId}/players`}
                icon={<Gamepad2 size={16} />}
              >
                Players
              </SidebarLink>
              <SidebarLink
                to={`/projects/${activeProjectId}/guilds`}
                icon={<Shield size={16} />}
              >
                Guilds
              </SidebarLink>
              <SidebarLink
                to={`/projects/${activeProjectId}/chat`}
                icon={<MessagesSquare size={16} />}
              >
                Chat
              </SidebarLink>
              {/* Bots live in guilds, so the entry point sits next to them. */}
              <SidebarLink
                to={`/projects/${activeProjectId}/bots`}
                icon={<Bot size={16} />}
              >
                Bots
              </SidebarLink>
              <SidebarLink
                to={`/projects/${activeProjectId}/leaderboards`}
                icon={<Trophy size={16} />}
              >
                Leaderboards
              </SidebarLink>
              <SidebarLink
                to={`/projects/${activeProjectId}/team`}
                icon={<UsersIcon size={16} />}
              >
                Team
              </SidebarLink>
              <SidebarLink
                to={`/projects/${activeProjectId}/api-keys`}
                icon={<Key size={16} />}
              >
                API Keys
              </SidebarLink>
              {/* Deliberately ungated: export is open to every project member,
                  only the Import card inside the page is super-admin only. */}
              <SidebarLink
                to={`/projects/${activeProjectId}/transfer`}
                icon={<ArrowRightLeft size={16} />}
              >
                Transfer
              </SidebarLink>
            </nav>
          )}

          <nav className={styles.section} aria-label="Platform">
            <div className={styles.sectionLabel}>Platform</div>
            <SidebarLink to="/projects" end icon={<Home size={16} />}>
              {isSuper ? "All projects" : "My projects"}
            </SidebarLink>
            {isSuper && (
              <SidebarLink to="/users" icon={<UsersIcon size={16} />}>
                Users
              </SidebarLink>
            )}
            {isSuper && (
              <SidebarLink to="/invites" icon={<Mail size={16} />}>
                Invites
              </SidebarLink>
            )}
          </nav>

          <div className={styles.footer}>
            <UserPopup
              name={user.name}
              email={user.email}
              role={user.role}
              onAccount={() => navigate("/me")}
              onSignOut={signOut}
            />
          </div>
        </aside>

        <main className={styles.main}>
          <Outlet />
        </main>
      </div>
    </>
  );
}

function SidebarLink({
  to,
  end,
  icon,
  children,
}: {
  to: string;
  end?: boolean;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        isActive ? `${styles.link} ${styles.linkActive}` : styles.link
      }
    >
      {icon && <span className={styles.linkIcon}>{icon}</span>}
      {children}
    </NavLink>
  );
}

interface PopupProps {
  name: string;
  email: string;
  role: string;
  onAccount: () => void;
  onSignOut: () => void;
}

function UserPopup({ name, email, role, onAccount, onSignOut }: PopupProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const isSuper = role === "super_admin";
  const roleLabel = isSuper ? "Super admin" : "Member";

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={styles.userWrap}>
      <button
        type="button"
        className={styles.userTrigger}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Avatar name={name} size={30} gradient={false} />
        <div className={styles.userText}>
          <div className={styles.userName}>{email}</div>
          <div className={styles.userSub}>{roleLabel}</div>
        </div>
        <MoreHorizontal
          size={14}
          aria-hidden="true"
          className={styles.userChevron}
        />
      </button>
      {open && (
        <div className={styles.userMenu} role="menu">
          <div className={styles.userMenuHeader}>
            <Avatar name={name} size={42} />
            <div className={styles.userMenuText}>
              <div className={styles.userMenuName}>{name}</div>
              <div className={styles.userMenuEmail}>{email}</div>
            </div>
            <Badge variant={isSuper ? "admin" : "user"}>{roleLabel}</Badge>
          </div>
          <div className={styles.userMenuSection}>
            <button
              type="button"
              role="menuitem"
              className={styles.menuBtn}
              onClick={() => {
                setOpen(false);
                onAccount();
              }}
            >
              <Settings
                size={14}
                aria-hidden="true"
                className={styles.menuBtnIcon}
              />
              <span className={styles.menuBtnLabel}>Account settings</span>
              <ChevronRight
                size={12}
                aria-hidden="true"
                className={styles.menuBtnChevron}
              />
            </button>
          </div>
          <hr className={styles.userMenuSep} />
          <div className={styles.userMenuSection}>
            <button
              type="button"
              role="menuitem"
              className={`${styles.menuBtn} ${styles.menuBtnDanger}`}
              onClick={() => {
                setOpen(false);
                onSignOut();
              }}
            >
              <LogOut
                size={14}
                aria-hidden="true"
                className={styles.menuBtnIcon}
              />
              <span className={styles.menuBtnLabel}>Sign out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
